import { useState, useEffect, forwardRef } from 'react';
import { HiX } from 'react-icons/hi';
import { FaSave } from 'react-icons/fa';
import _isEmpty from 'lodash/isEmpty';

import { Modal, ModalMethods, Text, Loader, toasts } from '../../../../../ui-component';
import { api } from '../../../../../utils/apiRequest';
import { getValueFromLocalStorage } from '../../../../../utils/common';

type OutfitPiece = {
  id: number;
  name: string;
  outfit_link?: string;
  icon_reference_id?: string;
};

type EditPieceModalProps = {
  piece: OutfitPiece | null;
  outfitName?: string;
  customOutfitId?: number;
  onSave: () => void;
};

const EditPieceModal = forwardRef<ModalMethods, EditPieceModalProps>(
  ({ piece, outfitName, customOutfitId, onSave }, ref) => {
    const [pieceName, setPieceName] = useState('');
    const [isSaving, setIsSaving] = useState(false);

    useEffect(() => {
      if (piece) {
        setPieceName(piece.name || '');
      } else {
        setPieceName('');
      }
    }, [piece]);

    const handleSave = async () => {
      const trimmedName = pieceName.trim();

      if (_isEmpty(trimmedName)) {
        toasts('error', 'Please enter a piece name', 'piece-name-required');

        return;
      }

      if (!piece) {
        toasts('error', 'Piece not found', 'piece-not-found');

        return;
      }

      const boutique_id = getValueFromLocalStorage('boutique_id');

      if (!boutique_id) {
        toasts('error', 'Boutique ID not found', 'boutique-id-missing');

        return;
      }

      setIsSaving(true);

      try {
        // Use existing icon_reference_id or generate one from piece name
        const iconRefId = null;

        const response = await api.putRequest(`outfit_piece/update/${piece.id}`, {
          name: trimmedName,
          boutique_id: Number(boutique_id),
          icon_reference_id: iconRefId,
          custom_outfit_id: customOutfitId,
        });

        if (response.status) {
          toasts('success', 'Piece name updated successfully', 'piece-update-success');
          onSave();

          if (typeof ref === 'object' && ref !== null && 'current' in ref && ref.current) {
            ref.current.hide();
          }
        } else {
          toasts('error', response.message || 'Failed to update piece name', 'piece-update-error');
        }
      } catch (err) {
        if (err instanceof Error) {
          toasts('error', err.message, 'piece-update-error');
        }
      } finally {
        setIsSaving(false);
      }
    };

    const headerComponent = (
      <div className="flex items-center justify-between w-full pr-8">
        <Text size="large" fontWeight={700} className="font-inter">
          Edit piece name
        </Text>
        <button
          type="button"
          onClick={() => {
            if (typeof ref === 'object' && ref !== null && 'current' in ref && ref.current) {
              ref.current.hide();
            }
          }}
          className="flex items-center gap-2 text-gray-600 hover:text-gray-800 transition-colors"
        >
          <Text size="small" color="black" className="font-inter">
            Close
          </Text>
          <HiX size={20} />
        </button>
      </div>
    );

    return (
      <Modal
        ref={ref}
        size="xl"
        headerComponent={headerComponent}
        showFooter={false}
        showCloseIcon={false}
      >
        <div className="flex flex-col min-h-[250px]">
          {outfitName && (
            <Text className="font-inter text-sm text-gray-600 mb-4 mt-1 block" color="black">
              Selected: {outfitName}
            </Text>
          )}

          <div className="flex flex-col gap-4">
            <div>
              <input
                type="text"
                placeholder="Piece name"
                value={pieceName}
                onChange={(e) => setPieceName(e.target.value)}
                className="w-full h-10 px-4 border border-gray-300 rounded-lg text-sm font-inter outline-none transition-colors focus:border-blue-500 focus:ring-2 focus:ring-blue-500 placeholder:text-gray-500"
                autoFocus
              />
            </div>

            <div className="flex justify-center pt-2">
              <button
                type="button"
                onClick={handleSave}
                disabled={isSaving || _isEmpty(pieceName.trim())}
                className="px-6 py-3 bg-[#4D7AFF] text-white rounded-lg font-semibold transition-all hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center gap-2"
              >
                {isSaving ? (
                  <>
                    <Loader />
                    <Text size="small" color="white">
                      Saving...
                    </Text>
                  </>
                ) : (
                  <>
                    <FaSave size={16} />
                    <Text size="small" color="white" className="font-inter font-medium">
                      Save Changes
                    </Text>
                  </>
                )}
              </button>
            </div>
          </div>
        </div>
      </Modal>
    );
  }
);

EditPieceModal.displayName = 'EditPieceModal';

export default EditPieceModal;
